import React from 'react';
import { createUseStyles } from 'react-jss';
import { S, labelStyle } from './shellTokens';

export type PanelId = 'outline' | 'insert' | 'pulse' | 'history';

export const PANELS: Array<{ id: PanelId; label: string; icon: string; hint: string }> = [
  { id: 'outline', label: 'Outline', icon: 'view_agenda', hint: 'Sections on this page' },
  { id: 'insert', label: 'Insert', icon: 'add_box', hint: 'Add a section' },
  { id: 'pulse', label: 'Pulse', icon: 'insights', hint: 'How the live page is doing' },
  { id: 'history', label: 'History', icon: 'history', hint: 'Versions, reviews and deployments' }
];

type Props = {
  open: PanelId | null;
  onOpen: (panel: PanelId | null) => void;
  /** Panels this person can't use (basic editors don't add sections). */
  hidden?: PanelId[];
  children: React.ReactNode;
};

const useStyles = createUseStyles({
  root: { display: 'flex', flexShrink: 0, height: '100%', fontFamily: S.ui },
  rail: {
    width: 64,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    padding: '10px 0',
    background: S.chrome,
    borderRight: `1px solid ${S.line}`
  },
  tab: {
    all: 'unset',
    cursor: 'pointer',
    width: 52,
    padding: '7px 0 6px',
    display: 'grid',
    justifyItems: 'center',
    gap: 2,
    borderRadius: 8,
    color: S.muted,
    fontFamily: S.label,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.02em',
    '& .material-icons': { fontSize: 21 },
    '&:hover, &:focus-visible': { background: S.chromeHover, color: S.fg }
  },
  active: { background: S.field, color: S.fg, boxShadow: `0 0 0 1px ${S.line}` },
  panel: {
    width: 300,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    background: S.chrome,
    borderRight: `1px solid ${S.line}`,
    minHeight: 0
  },
  head: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, padding: '14px 16px 10px' },
  title: { ...labelStyle, color: S.fg },
  hint: { fontSize: 12, color: S.muted, textAlign: 'right' },
  body: { flex: 1, overflowY: 'auto', padding: '4px 16px 24px', minHeight: 0 }
});

/** The tool rail on the left of the editor and the panel it opens. */
const EditorRail: React.FC<Props> = ({ open, onOpen, hidden = [], children }) => {
  const c = useStyles();
  const panels = PANELS.filter(p => !hidden.includes(p.id));
  const current = panels.find(p => p.id === open);
  return (
    <div className={c.root}>
      <nav className={c.rail} aria-label="Editor tools">
        {panels.map(p => (
          <button
            key={p.id}
            type="button"
            className={`${c.tab} ${open === p.id ? c.active : ''}`}
            aria-pressed={open === p.id}
            title={p.hint}
            data-rail={p.id}
            onClick={() => onOpen(open === p.id ? null : p.id)}
          >
            <i className="material-icons" aria-hidden>{p.icon}</i>
            <span>{p.label}</span>
          </button>
        ))}
      </nav>
      {current && (
        <section className={c.panel} aria-label={current.label} data-panel={current.id}>
          <div className={c.head}>
            <span className={c.title}>{current.label}</span>
            <span className={c.hint}>{current.hint}</span>
          </div>
          <div className={c.body}>{children}</div>
        </section>
      )}
    </div>
  );
};

export default EditorRail;
